export function snapshotWorkflowsTimeCost() {
  const slugOrder = [
    "meeting_heavy_days",
    "interruption_frequency",
    "dev_environment_toil",
    "dev_reload_time",
    "code_comprehension",
    "information_seeking",
    "ai_code_assistants",
    "test_wait_time",
    "review_wait_time",
    "ci_wait_time",
    "deployment_lead_time",
  ];

  return {
    items: [],
    maxValue: 0,
    noData: true,
    loading: true,
    originalItemsData: new Map(), // Store original data before filters

    get filtersApplied() {
      return (
        this.$store.snapshotResultFilters.tagIds.length ||
        this.$store.snapshotResultFilters.squadIds.length
      );
    },

    init() {
      // Watch for changes in the parent component's resultItemsLoaded property
      this.$watch("resultItemsLoaded", (value) => {
        if (value && this.resultItems) {
          // When parent data loads, update our local resultItems and re-initialize
          this.items = this.resultItems;
          this.processData();
        }
      });

      // Try to initialize with any data already available
      if (this.resultItems?.length) {
        this.items = this.resultItems;
        this.processData();
      }
    },

    processData() {
      // Reset the flags
      this.loading = false;
      this.noData = true;
      const hasFilters = this.filtersApplied;

      // Only process data if resultItems is available and has items
      if (!this.resultItems || !this.resultItems.length) {
        console.log("No result items available yet");
        // Don't create items for all slugs when there's no data
        this.items = [];
        this.maxValue = 1;
        this.noData = true;
        return;
      }

      // If no filters applied, store this as original data
      if (!hasFilters) {
        this.originalItemsData.clear();
        this.resultItems.forEach((item) => {
          if (item.slug && slugOrder.includes(item.slug)) {
            this.originalItemsData.set(item.slug, {
              hasData: item.annualized_time_cost_per_engineer !== null,
              value: item.annualized_time_cost_per_engineer,
            });
          }
        });
      }

      // Create a map of existing data by slug
      const dataMap = new Map();
      this.resultItems.forEach((item) => {
        if (item.slug && slugOrder.includes(item.slug)) {
          dataMap.set(item.slug, {
            ...item,
            value: item.annualized_time_cost_per_engineer,
            hasData: item.annualized_time_cost_per_engineer !== null,
          });
          this.noData = false;
        }
      });

      // Only include slugs that are present in the incoming data
      const availableSlugs = Array.from(dataMap.keys());

      // If we have no data at all after processing, don't create any items
      // The template will show the teaser state
      if (availableSlugs.length === 0) {
        this.items = [];
        this.maxValue = 1;
        this.noData = true;
        return;
      }

      // Create items only for slugs that exist in the data, sorted by value (highest to lowest)
      this.items = slugOrder
        .filter((slug) => availableSlugs.includes(slug))
        .map((slug) => {
          const existingData = dataMap.get(slug);
          const originalData = this.originalItemsData.get(slug);

          const item = existingData || {
            slug,
            name: this.getSlugDisplayName(slug),
            value: 0,
            hasData: false,
          };

          // Set hadDataBeforeFilters flag
          item.hadDataBeforeFilters =
            hasFilters && !item.hasData && originalData?.hasData === true;

          return item;
        })
        .sort((a, b) => {
          // Sort by value descending (highest to lowest)
          // Handle null/undefined values by treating them as 0
          const aValue = a.value || 0;
          const bValue = b.value || 0;
          return bValue - aValue;
        });

      this.maxValue = Math.max(...this.items.map((item) => item.value), 1);
    },

    getSlugDisplayName(slug) {
      const displayNames = {
        meeting_heavy_days: "Meeting heavy days",
        interruption_frequency: "Interruption frequency",
        dev_environment_toil: "Dev environment toil",
        dev_reload_time: "Dev reload time",
        code_comprehension: "Code comprehension",
        information_seeking: "Information seeking",
        ai_code_assistants: "AI time savings",
        test_wait_time: "Test wait time",
        review_wait_time: "Review wait time",
        ci_wait_time: "CI wait time",
        deployment_lead_time: "Deployment lead time",
      };
      return (
        displayNames[slug] ||
        slug.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      );
    },
    getBarWidth(value) {
      if (value === null) return null;
      if (!value) return "1%";
      const calculatedWidth = (value / this.maxValue) * 40;
      // Max width value here so that the bars with higher general time scales don't occupy way too much width
      const maxWidthOfAnyBar = 40;
      return calculatedWidth > maxWidthOfAnyBar
        ? `${maxWidthOfAnyBar}%`
        : `${calculatedWidth}%`;
    },
    getBarPosition(item) {
      const index = this.items.indexOf(item);
      const totalItems = this.items.length;
      return `${(index / Math.max(totalItems - 1, 1)) * 55}`;
    },
  };
}
